export const performancePosts = [
  {
    id: 56,
    slug: "mastering-core-web-vitals",
    title: "Mastering Core Web Vitals",
    excerpt: "Learn how to measure and optimize LCP, FID, and CLS to boost your SEO and user experience.",
    date: "February 01, 2024",
    readTime: "14 min read",
    category: "Performance",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>Speed as a Vital Sign</h2>
      <p>In 2024, performance is not optional. Google's Core Web Vitals have made site speed a direct ranking factor for SEO. More importantly, it's a direct metric for user satisfaction and conversion rates.</p>
      <h3>Largest Contentful Paint (LCP)</h3>
      <p>LCP measures when the main content of your page is visible. We'll show you how to identify your LCP element and optimize its loading through priority hints, image optimization, and critical CSS delivery.</p>
      <h3>Cumulative Layout Shift (CLS)</h3>
      <p>Nobody likes a page that jumps. We'll explore why layout shifts happen and how to prevent them by reserving space for images, ads, and dynamic content using standard CSS techniques.</p>
      <h3>Interaction to Next Paint (INP)</h3>
      <p>The successor to FID, INP measures the overall responsiveness of your page to user input. Learn how to identify long-running main thread tasks and break them up to keep your UI feeling instant.</p>
    `,
    keywords: "Core Web Vitals, SEO, Web Performance, LCP, CLS"
  },
  {
    id: 57,
    slug: "web-worker-and-off-main-thread-architecture",
    title: "Web Workers & Off-thread JS",
    excerpt: "Learn how to keep your UI snappy by moving heavy calculations to Web Workers and background threads.",
    date: "February 05, 2024",
    readTime: "11 min read",
    category: "Performance",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Main Thread is for UI</h2>
      <p>JavaScript is single-threaded, which means heavy calculations can freeze your interface. Web Workers provide a way to run scripts in background threads, keeping the main thread free to handle user interactions and animations.</p>
      <h3>Worker Communication</h3>
      <p>Learn the <code>postMessage</code> pattern and how to use libraries like <code>Comlink</code> to turn Worker communication into a simple, promise-based API that looks just like a normal function call.</p>
      <h3>When to Use Workers?</h3>
      <p>Discover the ideal use cases: image processing, massive data filtering, or complex mathematical models. We'll show you how to move these tasks to a separate thread without bloating your application architecture.</p>
      <h3>Partytown and Third-party Scripts</h3>
      <p>We'll look at modern tools like Partytown that allow you to run heavy third-party scripts (like Google Analytics or Facebook Pixel) in a web worker, reducing their impact on your page's performance.</p>
    `,
    keywords: "Web Workers, JavaScript Performance, Multi-threading"
  },
  {
    id: 58,
    slug: "optimizing-images-and-media",
    title: "Optimizing Images & Media",
    excerpt: "From AVIF to Lazy Loading: Master the techniques for delivering beautiful media without the bandwidth cost.",
    date: "February 10, 2024",
    readTime: "10 min read",
    category: "Performance",
    image: "https://images.unsplash.com/photo-1550439062-609e1531270e?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Weight of the Web</h2>
      <p>Images and videos account for over 60% of most page weights. Optimizing your media is the single most effective way to improve your site's load time and reduce data costs for your users.</p>
      <h3>Modern Formats: WebP and AVIF</h3>
      <p>Learn why you should stop using JPEGs and PNGs. We'll show you the massive compression benefits of AVIF and how to use the <code>&lt;picture&gt;</code> tag to serve the best format for every browser.</p>
      <h3>Intelligent Lazy Loading</h3>
      <p>Discover why <code>loading="lazy"</code> is just the beginning. We'll explore blurred placeholders, intersection observers, and libraries like <code>next/image</code> that automate the heavy lifting of media management.</p>
      <h3>Video Optimization</h3>
      <p>We'll talk about the impact of autoplaying videos and how to use streaming protocols or highly compressed formats to show high-quality motion without killing your page speed.</p>
    `,
    keywords: "Image Optimization, AVIF, WebP, Lazy Loading"
  },
  {
    id: 59,
    slug: "caching-strategies-for-frontend-devs",
    title: "Frontend Caching Guide",
    excerpt: "Master HTTP headers, Service Workers, and browser storage to build apps that work offline and load instantly.",
    date: "February 15, 2024",
    readTime: "13 min read",
    category: "Performance",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Best Request is No Request</h2>
      <p>Caching is the ultimate performance tool. By storing data closer to the user, you can make subsequent visits to your site feel near-instant, regardless of their connection speed.</p>
      <h3>HTTP Headers Mastery</h3>
      <p>Understand <code>Cache-Control</code>, <code>ETag</code>, and <code>Stale-While-Revalidate</code>. We'll show you how to configure your CDN and server to ensure your users always have the latest code while still benefiting from extreme caching.</p>
      <h3>Service Workers and PWAs</h3>
      <p>Discover the power of the "offline-first" mentality. Learn how Service Workers allow you to intercept network requests and serve cached content, even when the user has no internet connection at all.</p>
      <h3>IndexedDB and LocalStorage</h3>
      <p>We'll discuss where to store application state and data. Learn the pros and cons of different browser storage options and how to build a robust syncing strategy for your web apps.</p>
    `,
    keywords: "Browser Caching, HTTP Headers, Service Workers, PWA"
  },
  {
    id: 60,
    slug: "rendering-strategies-ssr-ssg-isr",
    title: "SSR, SSG, and ISR Explained",
    excerpt: "Understand the trade-offs between different rendering modes and how to pick the right one for your app's performance.",
    date: "February 20, 2024",
    readTime: "12 min read",
    category: "Performance",
    image: "https://images.unsplash.com/photo-1551033406-611cf9a28f67?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Rendering Spectrum</h2>
      <p>The choice between Static (SSG), Server-Side (SSR), and Incremental (ISR) rendering has a massive impact on your TTFB (Time to First Byte) and your overall search visibility.</p>
      <h3>Static Site Generation (SSG)</h3>
      <p>Learn when to pre-render everything at build time. It's the fastest option for the user, but we'll discuss the challenges of scaling SSG for thousands or millions of pages.</p>
      <h3>Incremental Static Regeneration (ISR)</h3>
      <p>Discover the "holy grail" of rendering. ISR allows you to update static content after you've built your site, without needing a full rebuild. It provides the speed of static with the flexibility of dynamic data.</p>
      <h3>Server-Side Rendering (SSR)</h3>
      <p>When do you actually need a server for every request? We'll discuss personalized content, real-time data needs, and how SSR ensures that your dynamic apps are fully readable by search engine crawlers.</p>
    `,
    keywords: "SSR, SSG, ISR, Next.js, Rendering Patterns"
  }
];
